import React, { useMemo, useState } from "react";
import { Mail, Send, Trash2 } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, ConfirmModal, FormField, Input, NativeSelect, StatusBadge,
} from "../ui";
import apiClient from "../../utils/apiClient";
import { getCurrentUser } from "../../utils/auth";
import { MAIL_PRESETS, MAIL_SECURITY, timeAgo } from "./communications";

const toForm = (s) => ({
  preset: s.preset || "custom",
  host: s.host || "",
  port: String(s.port || 587),
  security: s.secure ? "ssl" : "starttls",
  username: s.username || "",
  password: "",
  senderEmail: s.senderEmail || "",
  senderName: s.senderName || "",
  replyTo: s.replyTo || "",
  fallbackToPlatform: s.fallbackToPlatform !== false,
  enabled: !!s.enabled,
});

const toBody = (f) => ({
  preset: f.preset,
  host: f.host.trim(),
  port: Number(f.port),
  secure: f.security === "ssl",
  username: f.username.trim(),
  password: f.password,
  senderEmail: f.senderEmail.trim(),
  senderName: f.senderName.trim(),
  replyTo: f.replyTo.trim(),
  fallbackToPlatform: f.fallbackToPlatform,
});

// The fields a delivered test vouches for. A blank password means "the saved
// one", which is what the server tests with too.
const connectionKey = (f) => JSON.stringify([
  f.host.trim().toLowerCase(), String(f.port), f.security, f.username.trim(), f.password, f.senderEmail.trim().toLowerCase(),
]);

function statusOf(saved) {
  if (!saved.configured) return { status: "neutral", label: "Not set up" };
  if (!saved.enabled) return { status: "neutral", label: "Saved, switched off" };
  if (saved.health.consecutiveFailures > 0) return { status: "danger", label: "Failing" };
  return { status: "success", label: "Sending from your address" };
}

/**
 * Settings → Email & SMS → Email.
 *
 * The institute's own SMTP server. Mail to its students and staff then leaves
 * from its own address; the platform's billing mail to the institute never
 * does. "Send test email" tries the values on screen before anything is saved,
 * and the server refuses to switch on settings that have not delivered a test.
 */
export default function EmailServerSettings({ saved, platformSender, brandName, onSaved, showToast }) {
  const [form, setForm] = useState(() => toForm(saved));
  const [testTo, setTestTo] = useState(() => getCurrentUser()?.email || "");
  const [testedKey, setTestedKey] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(null); // 'save' | 'test' | 'remove'
  const [confirmRemove, setConfirmRemove] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const preset = MAIL_PRESETS.find((p) => p.value === form.preset) || MAIL_PRESETS[MAIL_PRESETS.length - 1];

  const savedKey = useMemo(() => connectionKey(toForm(saved)), [saved]);
  const key = connectionKey(form);
  const tested = testedKey === key || (saved.verified && key === savedKey);
  const status = statusOf(saved);

  const pickPreset = (value) => {
    const p = MAIL_PRESETS.find((x) => x.value === value);
    if (!p?.host) return set({ preset: value });
    set({ preset: value, host: p.host, port: String(p.port), security: p.secure ? "ssl" : "starttls" });
  };

  const pickSecurity = (security) => {
    const port = security === "ssl" && form.port === "587" ? "465"
      : security === "starttls" && form.port === "465" ? "587" : form.port;
    set({ security, port });
  };

  const sendTest = async () => {
    setBusy("test");
    setTestResult(null);
    try {
      const { data } = await apiClient.post("/institute/communications/email/test", { ...toBody(form), to: testTo.trim() });
      setTestedKey(key);
      setTestResult({ tone: "success", message: data.message });
    } catch (err) {
      setTestResult({ tone: "danger", message: err?.response?.data?.message || "The test email could not be sent." });
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await apiClient.put("/institute/communications/email", { ...toBody(form), enabled: form.enabled });
      showToast?.(data.message || "Email settings saved.");
      setForm(toForm(data.data));
      setTestedKey(null);
      onSaved?.(data.data);
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Could not save email settings.", "error");
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    setConfirmRemove(false);
    setBusy("remove");
    try {
      const { data } = await apiClient.delete("/institute/communications/email");
      showToast?.(data.message || "Removed.");
      const empty = { configured: false, enabled: false, verified: false, health: { consecutiveFailures: 0 } };
      setForm(toForm(empty));
      setTestResult(null);
      onSaved?.(empty);
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Could not remove email settings.", "error");
    } finally {
      setBusy(null);
    }
  };

  const failing = saved.enabled && saved.health.consecutiveFailures > 0 && saved.health.lastError;
  const blockedSave = form.enabled && !tested;

  return (
    <AppCard>
      <AppCardHeader>
        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <AppCardTitle>
              <span className="tw-inline-flex tw-items-center tw-gap-2">
                <Mail size={18} aria-hidden="true" /> Email
              </span>
            </AppCardTitle>
            <AppCardDescription>
              Emails to your students and staff come from <strong>{platformSender}</strong> with your institute's name.
              Connect your own mail server to send them from your own address instead.
            </AppCardDescription>
          </div>
          <StatusBadge status={status.status}>{status.label}</StatusBadge>
        </div>
      </AppCardHeader>

      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        {failing && (
          <Callout tone="danger">
            Your mail server failed {timeAgo(saved.health.lastFailureAt)}: {saved.health.lastError}{" "}
            {saved.fallbackToPlatform
              ? `Emails are being sent from ${platformSender} until it works again.`
              : "Emails are not being delivered until it works again."}
          </Callout>
        )}

        <section className="tw-flex tw-flex-col tw-gap-4" aria-label="Mail server">
          <FormField label="Email provider" hint={preset.hint}>
            <NativeSelect
              value={form.preset}
              onChange={(e) => pickPreset(e.target.value)}
              options={MAIL_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
            />
          </FormField>

          <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
            <FormField label="SMTP server" required>
              <Input value={form.host} onChange={(e) => set({ host: e.target.value })} placeholder="smtp.yourdomain.com" autoComplete="off" />
            </FormField>
            <div className="tw-grid tw-grid-cols-2 tw-gap-4">
              <FormField label="Security">
                <NativeSelect value={form.security} onChange={(e) => pickSecurity(e.target.value)} options={MAIL_SECURITY} />
              </FormField>
              <FormField label="Port" required>
                <Input type="number" inputMode="numeric" value={form.port} onChange={(e) => set({ port: e.target.value })} />
              </FormField>
            </div>
            <FormField label="Username" hint="Usually the full email address of the mailbox.">
              <Input value={form.username} onChange={(e) => set({ username: e.target.value })} autoComplete="off" />
            </FormField>
            <FormField
              label="Password"
              hint={saved.hasPassword ? "A saved password is never shown. Leave blank to keep it." : "Stored encrypted."}
            >
              <Input
                type="password"
                value={form.password}
                onChange={(e) => set({ password: e.target.value })}
                placeholder={saved.hasPassword ? "••••••••  (saved)" : ""}
                autoComplete="new-password"
              />
            </FormField>
          </div>
        </section>

        <section className="tw-flex tw-flex-col tw-gap-4" aria-label="Sender">
          <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">Who emails come from</h4>
          <div className="tw-grid tw-gap-4 md:tw-grid-cols-3">
            <FormField label="From address" required hint="Must be the mailbox above, or an address it may send as.">
              <Input type="email" value={form.senderEmail} onChange={(e) => set({ senderEmail: e.target.value })} placeholder="admissions@yourinstitute.com" />
            </FormField>
            <FormField label="From name" hint="Leave blank to use your institute's name.">
              <Input value={form.senderName} onChange={(e) => set({ senderName: e.target.value })} placeholder={brandName || "Your institute"} />
            </FormField>
            <FormField label="Reply-to address" hint="Optional. Where replies go.">
              <Input type="email" value={form.replyTo} onChange={(e) => set({ replyTo: e.target.value })} placeholder="office@yourinstitute.com" />
            </FormField>
          </div>
          <Checkbox
            checked={form.fallbackToPlatform}
            onChange={(e) => set({ fallbackToPlatform: e.target.checked })}
            label={`If your mail server fails, send from ${platformSender} instead`}
            description="Recommended. A password reset or login code that arrives from our address is better than one that never arrives."
          />
        </section>

        <section className="tw-flex tw-flex-col tw-gap-3 tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-4" aria-label="Test">
          <h4 className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">1. Send a test email</h4>
          <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
            <FormField label="Send the test to" className="tw-min-w-[16rem] tw-flex-1">
              <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
            </FormField>
            <AppButton variant="outline" onClick={sendTest} disabled={busy !== null || !testTo.trim() || !form.host.trim()}>
              <Send size={16} aria-hidden="true" />
              {busy === "test" ? "Sending…" : "Send test email"}
            </AppButton>
          </div>
          {testResult && <Callout tone={testResult.tone} role="alert">{testResult.message}</Callout>}

          <h4 className="tw-m-0 tw-mt-2 tw-text-sm tw-font-semibold tw-text-content">2. Switch it on</h4>
          <Checkbox
            checked={form.enabled}
            disabled={!form.enabled && !tested}
            onChange={(e) => set({ enabled: e.target.checked })}
            label="Send emails from this address"
            description={tested
              ? "Welcome and login emails, password resets, fee receipts and reminders, enquiry replies, certificates and class alerts. Your LMS subscription emails still come from us."
              : "Send a test email with these settings first."}
          />
          {blockedSave && (
            <Callout tone="warning">
              These settings have not delivered a test yet. Send a test email, or untick the box to save them switched off.
            </Callout>
          )}
        </section>

        <div className="tw-flex tw-flex-wrap tw-gap-2">
          <AppButton onClick={save} disabled={busy !== null || blockedSave}>
            {busy === "save" ? "Saving…" : "Save email settings"}
          </AppButton>
          {saved.configured && (
            <AppButton variant="outline-destructive" onClick={() => setConfirmRemove(true)} disabled={busy !== null}>
              <Trash2 size={16} aria-hidden="true" />
              Remove
            </AppButton>
          )}
        </div>

        <Callout tone="info">
          So your emails reach the inbox and not spam, add the SPF and DKIM records your mail provider gives you to your
          domain's DNS. Without them, providers such as Gmail may reject mail sent from your address.
        </Callout>
      </AppCardContent>

      <ConfirmModal
        show={confirmRemove}
        title="Remove your mail server?"
        message={`Emails will come from ${platformSender} again. The saved password is deleted.`}
        confirmText="Remove"
        variant="danger"
        onConfirm={remove}
        onHide={() => setConfirmRemove(false)}
      />
    </AppCard>
  );
}
